"use client";

import { useEffect, useMemo, useState, type ComponentProps } from "react";
import { SignUp } from "@clerk/nextjs";
import {
  buildSignupAttributionFromSearch,
  SIGNUP_ATTRIBUTION_METADATA_KEY,
  SIGNUP_LAST_ATTRIBUTION_METADATA_KEY,
} from "@/lib/signup-attribution";
import {
  mergeAttribution,
  normalizeAttribution,
  type Attribution,
} from "@/lib/attribution";
import {
  readBrowserAttribution,
  readAdvertisingConsent,
  readStoredAttribution,
  readStoredLastAttribution,
} from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import {
  isAnalyticsSuppressedUrl,
  isSuppressedUrlString,
} from "@/lib/analytics/suppressed-paths";
import { safeInternalPath } from "@/lib/safe-internal-path";
import { withRedirectUrl } from "@/lib/auth-redirect-url";

type AttributedSignUpProps = ComponentProps<typeof SignUp>;
type SignupAttributionMetadata = {
  first: Attribution | null;
  last: Attribution | null;
};

const CLERK_SIGNUP_REDIRECT_SEARCH_PARAMS = [
  "redirect_url",
  "after_sign_up_url",
  "sign_up_force_redirect_url",
  "sign_up_fallback_redirect_url",
];

function hasClerkSignupRedirectSearchParam(): boolean {
  const params = new URLSearchParams(window.location.search);
  return CLERK_SIGNUP_REDIRECT_SEARCH_PARAMS.some((param) => {
    const target = params.get(param);
    return target !== null && !isHomeRedirectTarget(target);
  });
}

function isHomeRedirectTarget(target: string): boolean {
  if (!target.trim()) return true;

  try {
    const url = new URL(target, window.location.origin);
    const hostname = url.hostname.toLowerCase();
    const currentHostname = window.location.hostname.toLowerCase();
    const isKnownAppHost =
      hostname === currentHostname ||
      hostname === "thesitebook.co.uk" ||
      hostname === "www.thesitebook.co.uk" ||
      hostname === "staging.thesitebook.co.uk";

    return isKnownAppHost && url.pathname === "/";
  } catch {
    return false;
  }
}

function readSignupRedirectTargetFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  for (const param of CLERK_SIGNUP_REDIRECT_SEARCH_PARAMS) {
    const target = params.get(param);
    if (target === null || isHomeRedirectTarget(target)) continue;
    const internal = safeInternalPath(target);
    if (internal) return internal;
  }
  return null;
}

function readCurrentUrlAttribution(): Attribution | null {
  const params = new URLSearchParams(window.location.search);
  // Never stamp a token-bearing landing page / source path / referrer into
  // signup attribution — it reaches Clerk unsafeMetadata and the signup Slack
  // alert. The team-invite flow lands here as /signup?sign_up_force_redirect_url
  // =%2Fteam%2Fjoin%2F<token>. utm/gclid/ref params (read from `params`) are
  // unaffected.
  const suppressed = isAnalyticsSuppressedUrl(
    window.location.pathname,
    window.location.search,
    window.location.hash,
  );
  const rawReferrer = document.referrer || null;
  return normalizeAttribution(
    buildSignupAttributionFromSearch(
      params,
      isSuppressedUrlString(rawReferrer) ? null : rawReferrer,
      suppressed ? "" : `${window.location.pathname}${window.location.search}`,
      suppressed ? "" : window.location.pathname,
      new Date().toISOString(),
    ),
  );
}

function readBrowserSignupAttribution(): Attribution | null {
  if (readStoredAttribution()) return readBrowserAttribution();
  return mergeAttribution(
    readCurrentUrlAttribution(),
    readBrowserAttribution(),
  );
}

function readSignupAttributionMetadata(): SignupAttributionMetadata {
  // Signup metadata is durable and later reaches server-side conversion
  // processing. Do not stamp URL, stored, click-ID or analytics attribution
  // unless the authoritative first-party cookie currently grants it.
  if (readAdvertisingConsent() !== "accepted") {
    return { first: null, last: null };
  }
  const first = readBrowserSignupAttribution();
  const lastTouch = readStoredLastAttribution() ?? readCurrentUrlAttribution();
  const last = lastTouch ? mergeAttribution(first, lastTouch) : first;
  return { first, last };
}

export function AttributedSignUp({
  fallbackRedirectUrl = "/onboarding",
  forceRedirectUrl,
  // Keep the "Already have an account? Sign in" link (and the transfer Clerk
  // performs when the email is already registered) on our own first-party
  // /login page instead of the Clerk Account Portal. The signup + login pages
  // use catch-all path routing ([[...signup]] / [[...login]]) and we set no
  // NEXT_PUBLIC_CLERK_SIGN_IN_URL, so without this Clerk defaults that link to
  // the hosted portal (accounts.dev on staging). A returning user who hit
  // "account already registered" then bounced through the portal back to
  // /signup instead of landing on /dashboard — the redirect loop this fixes.
  // When no explicit signInUrl is given, the default also carries the caller's
  // redirect intent (signInForceRedirectUrl prop or a Clerk redirect search
  // param) as /login?redirect_url=… so checkout-resume / team-invite / funnel
  // flows survive the switch to the sign-in form.
  signInUrl,
  unsafeMetadata,
  ...props
}: AttributedSignUpProps) {
  const [hasExplicitRedirect] = useState(() => {
    if (typeof window === "undefined") return false;
    return hasClerkSignupRedirectSearchParam();
  });
  const [urlSignInRedirectTarget] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return readSignupRedirectTargetFromUrl();
  });
  const [attribution, setAttribution] = useState<SignupAttributionMetadata>(
    () => {
      if (typeof window === "undefined") return { first: null, last: null };
      return readSignupAttributionMetadata();
    },
  );

  useEffect(() => {
    const refreshAttribution = () => {
      // Re-read the authoritative cookie rather than trusting event detail.
      // This preserves the explicit-Accept flow for visitors already on the
      // signup page and removes metadata again after a later refusal.
      setAttribution(readSignupAttributionMetadata());
    };
    return subscribeToBrowserConsentCategory("advertising", refreshAttribution);
  }, []);

  const mergedUnsafeMetadata = useMemo(() => {
    const baseMetadata = { ...(unsafeMetadata ?? {}) };
    // These keys are owned by this consent gate. A caller cannot smuggle a
    // pre-stamped attribution object through unsafeMetadata after refusal.
    delete baseMetadata[SIGNUP_ATTRIBUTION_METADATA_KEY];
    delete baseMetadata[SIGNUP_LAST_ATTRIBUTION_METADATA_KEY];
    if (!attribution.first && !attribution.last) {
      return Object.keys(baseMetadata).length > 0 ? baseMetadata : undefined;
    }
    return {
      ...baseMetadata,
      ...(attribution.first
        ? { [SIGNUP_ATTRIBUTION_METADATA_KEY]: attribution.first }
        : {}),
      ...(attribution.last
        ? { [SIGNUP_LAST_ATTRIBUTION_METADATA_KEY]: attribution.last }
        : {}),
    };
  }, [attribution, unsafeMetadata]);

  const resolvedForceRedirectUrl =
    forceRedirectUrl === undefined && !hasExplicitRedirect
      ? fallbackRedirectUrl
      : forceRedirectUrl;
  const redirectProps =
    resolvedForceRedirectUrl === undefined
      ? { fallbackRedirectUrl }
      : { fallbackRedirectUrl, forceRedirectUrl: resolvedForceRedirectUrl };

  // Only same-app paths ride along — an absolute/external target never becomes
  // a /login?redirect_url=… value (LoginPage would drop it anyway).
  const signInRedirectTarget =
    safeInternalPath(props.signInForceRedirectUrl) ?? urlSignInRedirectTarget;
  const resolvedSignInUrl =
    signInUrl ?? withRedirectUrl("/login", signInRedirectTarget);

  return (
    <SignUp
      {...props}
      {...redirectProps}
      signInUrl={resolvedSignInUrl}
      unsafeMetadata={mergedUnsafeMetadata}
    />
  );
}
